import { Combobox, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, FormNumberInput, RadioGroup, RadioGroupItem, Text } from "@sentricmusic/ui";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { CapacityOption, SectionKey } from "../types/gigs.types";
import type { GigClaimFormValues } from "../utils/gigsClaimSchema";

import { FLAGS } from "../utils/gigsClaimUtils";

const capacityOptions: CapacityOption[] = [
  { value: "Under 15,000", label: "Under 15,000" },
  {
    value: "Over 15,000",
    label: "Over 15,000",
  },
];

const str = (s: string | null): string => s ?? "";

function AdmissionSection() {
  const { t } = useTranslation(["gigs"]);
  const { control } = useFormContext<GigClaimFormValues>();
  const country = useWatch({ control, name: "country" });
  const venueCapacity = useWatch({ control, name: "venueCapacity" },
  );
  const sections = useMemo<SectionKey[]>(() => {
    const flag = FLAGS.find(f => f.id === country) ?? FLAGS.find(f => f.id === "default")!;
    return flag.onSections;
  }, [country]);

  const admissionSectionKeys: SectionKey[] = [
    "admissionFee",
    "admissionCAD",
    "admissionDKK",
    "admissionEUR",
    "audienceNumber",
    "eventName",
    "otherActs",
    "venueCapacity",
  ];

  const hasAdmissionFields = sections.some(s => admissionSectionKeys.includes(s));
  if (!hasAdmissionFields)
    return null;

  // handle legacy venue capacity values - in update gig claim flow
  const dataWithLegacy = venueCapacity != null && venueCapacity.trim() !== null && !capacityOptions.some(o => o.value === venueCapacity) ? [...capacityOptions, { value: venueCapacity, label: `${venueCapacity}` }] : capacityOptions;

  return (
    <>
      <div className="flex flex-col">
        <Text as="subtitle">{t("gigs:admission-header", "Admission")}</Text>
        <div className="flex flex-col gap-2">

          {sections.includes("venueCapacity") && (
            <FormField
              control={control}
              name="venueCapacity"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Combobox
                      label={str(t("gigs:venue-capacity-placeholder", "Venue Capacity"))}
                      placeholder={str(t("gigs:venue-capacity-placeholder", "Venue Capacity"))}
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      data={dataWithLegacy}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {sections.includes("admissionFee") && (
            <FormField
              control={control}
              name="admissionFee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>
                    {t("gigs:admission-fee", "Was an admission fee charged for this performance?")}
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      className="flex flex-row gap-4"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem id="admissionFee-yes" value="Yes" />
                        <label htmlFor="admissionFee-yes" className="text-sm font-sentric-alt cursor-pointer">
                          {t("common:yes", "Yes")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem id="admissionFee-no" value="No" />
                        <label htmlFor="admissionFee-no" className="text-sm font-sentric-alt cursor-pointer">
                          {t("common:no", "No")}
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {sections.includes("admissionCAD") && (
            <FormField
              control={control}
              name="admissionCAD"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("gigs:admission-fee-cad-placeholder", "Price of Admission ($CAD)")}</FormLabel>
                  <FormControl>
                    <FormNumberInput
                      className="w-full"
                      placeholder={str(t("gigs:admission-fee-cad-placeholder", "Price of Admission ($CAD)"))}
                      value={field.value ?? ""}
                      onChange={e => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {sections.includes("admissionDKK") && (
            <FormField
              control={control}
              name="admissionDKK"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("gigs:admission-fee-dkk-placeholder", "Price of Admission (DKK)")}</FormLabel>
                  <FormControl>
                    <FormNumberInput
                      className="w-full"
                      placeholder={str(t("gigs:admission-fee-dkk-placeholder", "Price of Admission (DKK)"))}
                      value={field.value ?? ""}
                      onChange={e => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {sections.includes("audienceNumber") && (
            <FormField
              control={control}
              name="audienceNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("gigs:audience-number", "Audience number")}</FormLabel>
                  <FormControl>
                    <FormNumberInput
                      className="w-full"
                      placeholder={str(t("gigs:audience-number", "Audience number"))}
                      value={field.value ?? ""}
                      onChange={e => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {sections.includes("eventName") && (
            <FormField
              control={control}
              name="eventName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("gigs:event-name", "Event name")}</FormLabel>
                  <FormControl>
                    <FormInput
                      placeholder={str(t("gigs:event-name", "Event name"))}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {sections.includes("otherActs") && (
            <FormField
              control={control}
              name="otherActs"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("gigs:other-acts", "Other acts")}</FormLabel>
                  <FormControl>
                    <FormInput
                      placeholder={str(t("gigs:other-acts", "Other acts"))}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {sections.includes("admissionEUR") && (
            <FormField
              control={control}
              name="admissionEUR"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("gigs:es-ticket-price-place-holder", "Price of Admission (€EUR)")}</FormLabel>
                  <FormControl>
                    <FormNumberInput
                      className="w-full"
                      placeholder={str(t("gigs:es-ticket-price-place-holder", "Price of Admission (€EUR)"))}
                      value={field.value ?? ""}
                      onChange={e => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default AdmissionSection;
